import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth, useSubscription, Logo, LogoIcon, ICONS, getIconSize, getIconColor } from '@talktoast/shared'
import { SubscriptionBadge } from './subscription/SubscriptionBadge'

export interface HeaderProps {
  /**
   * Optional callback for navigation (used for view state management)
   * If provided, this will be called instead of React Router navigation
   *
   * @param path - The navigation path (e.g., '/settings', '/settings/dietary')
   */
  onNavigate?: (path: string) => void
}

/**
 * Header Component
 * Sticky header with logo, notifications, and profile dropdown
 * Replaces need for Settings in bottom nav
 */
export default function Header({ onNavigate }: HeaderProps = {}) {
  const navigate = useNavigate()
  const { user, profile, signOut } = useAuth()
  const { subscription } = useSubscription()
  const [showProfileMenu, setShowProfileMenu] = useState(false)
  const profileRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setShowProfileMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleProfileMenuClick = (path: string) => {
    setShowProfileMenu(false)

    // Use custom navigation callback if provided (for view state management)
    // Otherwise fall back to React Router navigation
    if (onNavigate) {
      onNavigate(path)
    } else {
      navigate(path)
    }
  }

  const handleSignOut = async () => {
    setShowProfileMenu(false)
    await signOut()
    navigate('/app')
  }

  // Derive display label for the nav chip: first name on mobile, full name on desktop
  const displayName = profile?.displayName || user?.email?.split('@')[0] || ''
  const firstNameChip = user ? (displayName.split(' ')[0] || 'Account') : 'Sign in'

  // First initial for avatar circle (authenticated users only)
  const firstInitial = user ? displayName.charAt(0).toUpperCase() : null

  return (
    <header className="sticky top-0 z-30 bg-white border-b border-gray-200 shadow-sm">
      <div className="flex items-center justify-between px-4 py-2 h-14 sm:h-12">
        {/* Logo - Desktop: Full logo, Mobile: Icon only */}
        <button
          onClick={() => navigate('/app')}
          className="focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-toast-gold rounded"
          aria-label="Go to home"
        >
          {/* Desktop: Full logo with text */}
          <div className="hidden sm:block">
            <Logo variant="full" size="h-8" alt="TalkToast - AI Meal Planning" />
          </div>
          {/* Mobile: Icon only */}
          <div className="sm:hidden">
            <LogoIcon size="h-8" alt="TalkToast" />
          </div>
        </button>

        {/* Right Actions */}
        <div className="flex items-center gap-2">
          {/* Profile Dropdown */}
          <div className="relative" ref={profileRef}>
            <button
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
              title="Account menu"
            >
              {/* Avatar circle: first initial for authenticated users, generic icon for guests */}
              <div className="w-8 h-8 rounded-full bg-toast-gold/10 flex items-center justify-center flex-shrink-0">
                {firstInitial ? (
                  <span className="text-sm font-bold text-toast-gold leading-none">
                    {firstInitial}
                  </span>
                ) : (
                  <ICONS.user.profile className={`${getIconSize('sm')} text-toast-gold`} />
                )}
              </div>

              {/* Mobile (<sm): show first name chip for identity at a glance */}
              <span className="sm:hidden text-sm font-medium text-gray-700 max-w-[80px] truncate">
                {firstNameChip}
              </span>

              {/* Desktop (sm+): show full display name */}
              <span className="hidden sm:inline text-sm font-medium text-gray-700 max-w-[120px] truncate">
                {user ? displayName : 'Sign in'}
              </span>
            </button>

            {/* Dropdown Menu */}
            {showProfileMenu && (
              <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                {/* User Info */}
                <div className="px-4 py-3 border-b border-gray-200">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-body font-semibold text-gray-900 truncate">
                      {profile?.displayName || 'Guest User'}
                    </p>
                    {subscription && (
                      <SubscriptionBadge tier={subscription.tier} size="sm" />
                    )}
                  </div>
                  <p className="text-caption text-gray-600 truncate">
                    {user?.email || 'Not signed in'}
                  </p>
                </div>

                {/* Menu Items */}
                <div className="py-1">
                  {/* Account */}
                  <button
                    onClick={() => handleProfileMenuClick('/settings')}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <ICONS.user.profile className={`${getIconSize('sm')} text-gray-500`} />
                    <span>Account</span>
                  </button>

                  {/* Preferences - Consolidated */}
                  <button
                    onClick={() => handleProfileMenuClick('/settings/preferences')}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <ICONS.utility.filters className={`${getIconSize('sm')} text-gray-500`} />
                    <span>Preferences</span>
                  </button>
                </div>

                {/* Sign Out */}
                {user && (
                  <div className="border-t border-gray-200 py-1 mt-1">
                    <button
                      onClick={handleSignOut}
                      className="w-full flex items-center gap-3 px-4 py-2 text-body-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <ICONS.user.logout className={getIconSize('sm')} />
                      <span>Sign Out</span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  )
}
